import React, { useState, useEffect } from 'react';
import { generateClient } from 'aws-amplify/data';
import type { Schema } from '../amplify/data/resource';
import StickyFooter from '../components/StickyFooter';
import { uploadData, getUrl, list } from 'aws-amplify/storage';

interface Tutor {
  id: string;
  name: string;
  photo: string;
  bio: string;
  shortBio?: string;
  research?: string;
  projects?: string;
  references?: string;
  training?: string;
  expertise: string;
  experience: string;
  education: string;
  company: string;
  linkedin?: string;
  twitter?: string;
  website?: string;
  // Reference Projects (up to 5)
  referenceProjectTitle1?: string;
  referenceProjectSummary1?: string;
  referenceProjectLink1?: string;
  referenceProjectTitle2?: string;
  referenceProjectSummary2?: string;
  referenceProjectLink2?: string;
  referenceProjectTitle3?: string;
  referenceProjectSummary3?: string;
  referenceProjectLink3?: string;
  referenceProjectTitle4?: string;
  referenceProjectSummary4?: string;
  referenceProjectLink4?: string;
  referenceProjectTitle5?: string;
  referenceProjectSummary5?: string;
  referenceProjectLink5?: string;
  // Press Articles (up to 5)
  pressArticleTitle1?: string;
  pressArticleUrl1?: string;
  pressArticleTitle2?: string;
  pressArticleUrl2?: string;
  pressArticleTitle3?: string;
  pressArticleUrl3?: string;
  pressArticleTitle4?: string;
  pressArticleUrl4?: string;
  pressArticleTitle5?: string;
  pressArticleUrl5?: string;
  isFemale?: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface AdminTutorEditorProps {
  selectedTutorId?: string; // If provided, edit mode; if not, create mode
  onBack?: () => void;
  tutorId?: string; // If provided, edit mode; if not, create mode
  onSave?: (tutor: Tutor) => void;
  onCancel?: () => void;
}

const client = generateClient<Schema>({ authMode: 'userPool' });

const AdminTutorEditor: React.FC<AdminTutorEditorProps> = ({
  selectedTutorId: propSelectedTutorId,
  onBack,
  tutorId,
  onSave,
  onCancel
}) => {
  const [selectedTutorId, setSelectedTutorId] = useState<string>(propSelectedTutorId || tutorId || '');
  const [availableTutors, setAvailableTutors] = useState<{ id: string; name: string }[]>([]);
  
  const [formData, setFormData] = useState({
    name: '',
    photo: '',
    bio: '',
    role: '',
    shortBio: '',
    research: '',
    projects: '',
    references: '',
    training: '',
    expertise: '',
    experience: '',
    education: '',
    company: '',
    linkedin: '',
    scholar: '',
    twitter: '',
    website: '',
    // Reference Projects (up to 5)
    referenceProjectTitle1: '',
    referenceProjectSummary1: '',
    referenceProjectLink1: '',
    referenceProjectTitle2: '',
    referenceProjectSummary2: '',
    referenceProjectLink2: '',
    referenceProjectTitle3: '',
    referenceProjectSummary3: '',
    referenceProjectLink3: '',
    referenceProjectTitle4: '',
    referenceProjectSummary4: '',
    referenceProjectLink4: '',
    referenceProjectTitle5: '',
    referenceProjectSummary5: '',
    referenceProjectLink5: '',
    // Press Articles (up to 5)
    pressArticleTitle1: '',
    pressArticleUrl1: '',
    pressArticleTitle2: '',
    pressArticleUrl2: '',
    pressArticleTitle3: '',
    pressArticleUrl3: '',
    pressArticleTitle4: '',
    pressArticleUrl4: '',
    pressArticleTitle5: '',
    pressArticleUrl5: '',
    isFemale: false,
    isActive: true
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'warning' | 'info'; text: string } | null>(null);
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string>('');
  const [photoUploading, setPhotoUploading] = useState<boolean>(false);
  const [existingPhotoItems, setExistingPhotoItems] = useState<{ key: string; label: string }[]>([]);
  const [loadingExistingPhotos, setLoadingExistingPhotos] = useState<boolean>(false);
  const [photoRetryCount, setPhotoRetryCount] = useState<number>(0);
  // S3 diagnostics
  const [s3CheckRunning, setS3CheckRunning] = useState<boolean>(false);
  const [s3CheckMessage, setS3CheckMessage] = useState<string>('');
  const [s3TestUrl, setS3TestUrl] = useState<string>('');
  const [s3TestKey, setS3TestKey] = useState<string>('');
  // Collapsible section states - using object to make state management more robust
  const [collapsibleStates, setCollapsibleStates] = useState({
    referenceProjects: true,
    pressArticles: true,
    profileLinks: true,
    profilePhoto: true,
    profileContent: true
  });

  // Helper function to toggle collapsible sections
  const toggleCollapsibleSection = (section: keyof typeof collapsibleStates) => {
    setCollapsibleStates(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const isEditMode = Boolean(selectedTutorId);

  useEffect(() => {
    loadAvailableTutors();
  }, []);

  useEffect(() => {
    if (propSelectedTutorId) {
      setSelectedTutorId(propSelectedTutorId);
      loadTutor(propSelectedTutorId);
    }
  }, [propSelectedTutorId]);

  useEffect(() => {
    if (isEditMode && selectedTutorId) {
      loadTutor(selectedTutorId);
    }
  }, [selectedTutorId, isEditMode]);

  // Cleanup blob URLs on unmount
  useEffect(() => {
    return () => {
      if (photoPreview && photoPreview.startsWith('blob:')) {
        URL.revokeObjectURL(photoPreview);
      }
    };
  }, [photoPreview]);

  // Resolve formData.photo to preview URL (supports S3 public key or absolute URL)
  useEffect(() => {
    (async () => {
      const path = formData.photo || '';
      if (!path) {
        setPhotoPreview('');
        return;
      }
      try {
        if (path.startsWith('http') || path.startsWith('/')) {
          setPhotoPreview(path);
          return;
        }
        const publicPath = path.startsWith('public/') ? path : `public/${path}`;
        const { url } = await getUrl({ path: publicPath });
        setPhotoPreview(url.toString());
      } catch (e) {
        // Fallbacks: if the path looks like an S3 key (contains a slash), skip legacy path and use default placeholder
        if (path.includes('/')) {
          setPhotoPreview('/assets/images/tutors/default-tutor.svg');
        } else {
          setPhotoPreview(`/assets/images/tutors/${path}`);
        }
      }
    })();
  }, [formData.photo]);

  // Load existing tutor photos from S3 (public/tutors/...)
  const loadExistingTutorPhotos = async () => {
    try {
      setLoadingExistingPhotos(true);
      const collected: { key: string; label: string }[] = [];

      let nextToken: string | undefined = undefined;
      do {
        const { items: listed, nextToken: nt } = await list({
          path: 'public/tutors/',
          options: { recursive: true, nextToken }
        } as any);
        for (const it of listed || []) {
          const raw = (it.path || '').replace(/^\//, '');
          if (!raw || raw.endsWith('/')) continue;
          const displayKey = raw.startsWith('public/') ? raw.substring('public/'.length) : raw;
          const fileName = displayKey.split('/').pop() || displayKey;
          collected.push({ key: displayKey, label: fileName });
        }
        nextToken = nt as any;
      } while (nextToken);

      collected.sort((a, b) => a.label.localeCompare(b.label));
      setExistingPhotoItems(collected);
    } catch (e) {
      console.warn('Failed to list tutor photos:', e);
      setExistingPhotoItems([]);
    } finally {
      setLoadingExistingPhotos(false);
    }
  };

  useEffect(() => {
    loadExistingTutorPhotos();
  }, []);

  const loadAvailableTutors = async () => {
    try {
      console.log('🔍 Loading available tutors...');
      const { data } = await client.models.Tutor.list();
      console.log('📝 Available tutors loaded:', data);
      
      const tutors = (data || []).map((tutor: any) => ({
        id: tutor.id,
        name: tutor.name || 'Unnamed Tutor'
      }));
      
      setAvailableTutors(tutors);
    } catch (error) {
      console.error('❌ Error loading available tutors:', error);
      setAvailableTutors([]);
    }
  };

  const handleTutorSelection = (tutorId: string) => {
    setSelectedTutorId(tutorId);
    if (tutorId) {
      loadTutor(tutorId);
    } else {
      // Reset to create mode
      setFormData({
        name: '',
        photo: '',
        bio: '',
        role: '',
        shortBio: '',
        research: '',
        projects: '',
        references: '',
        training: '',
        expertise: '',
        experience: '',
        education: '',
        company: '',
        linkedin: '',
        scholar: '',
        twitter: '',
        website: '',
        // Reference Projects (up to 5)
        referenceProjectTitle1: '',
        referenceProjectSummary1: '',
        referenceProjectLink1: '',
        referenceProjectTitle2: '',
        referenceProjectSummary2: '',
        referenceProjectLink2: '',
        referenceProjectTitle3: '',
        referenceProjectSummary3: '',
        referenceProjectLink3: '',
        referenceProjectTitle4: '',
        referenceProjectSummary4: '',
        referenceProjectLink4: '',
        referenceProjectTitle5: '',
        referenceProjectSummary5: '',
        referenceProjectLink5: '',
        // Press Articles (up to 5)
        pressArticleTitle1: '',
        pressArticleUrl1: '',
        pressArticleTitle2: '',
        pressArticleUrl2: '',
        pressArticleTitle3: '',
        pressArticleUrl3: '',
        pressArticleTitle4: '',
        pressArticleUrl4: '',
        pressArticleTitle5: '',
        pressArticleUrl5: '',
        isFemale: false,
        isActive: true
      });
      // Clean up blob URL if it exists
      if (photoPreview && photoPreview.startsWith('blob:')) {
        URL.revokeObjectURL(photoPreview);
      }
      setPhotoFile(null);
      setPhotoPreview('');
    }
    setErrors({});
  };

  const loadTutor = async (tutorId: string) => {
    try {
      setLoading(true);
      console.log('🔍 Loading tutor:', tutorId);
      
      const { data } = await client.models.Tutor.get({ 
        id: tutorId,
        selectionSet: [
          'id', 'name', 'photo', 'bio', 'role', 'shortBio', 'research', 'projects', 'references', 'training',
          'expertise', 'experience', 'education', 'company', 'linkedin', 'scholar', 'twitter', 'website',
          'referenceProjectTitle1', 'referenceProjectSummary1', 'referenceProjectLink1',
          'referenceProjectTitle2', 'referenceProjectSummary2', 'referenceProjectLink2',
          'referenceProjectTitle3', 'referenceProjectSummary3', 'referenceProjectLink3',
          'referenceProjectTitle4', 'referenceProjectSummary4', 'referenceProjectLink4',
          'referenceProjectTitle5', 'referenceProjectSummary5', 'referenceProjectLink5',
          'pressArticleTitle1', 'pressArticleUrl1',
          'pressArticleTitle2', 'pressArticleUrl2',
          'pressArticleTitle3', 'pressArticleUrl3',
          'pressArticleTitle4', 'pressArticleUrl4',
          'pressArticleTitle5', 'pressArticleUrl5',
          'isFemale', 'isActive'
        ]
      });
      console.log('📝 Tutor loaded:', data);
      console.log('🔍 Reference projects in loaded data:', {
        project1: {
          title: (data as any)?.referenceProjectTitle1,
          summary: (data as any)?.referenceProjectSummary1,
          link: (data as any)?.referenceProjectLink1
        },
        project2: {
          title: (data as any)?.referenceProjectTitle2,
          summary: (data as any)?.referenceProjectSummary2,
          link: (data as any)?.referenceProjectLink2
        },
        project3: {
          title: (data as any)?.referenceProjectTitle3,
          summary: (data as any)?.referenceProjectSummary3,
          link: (data as any)?.referenceProjectLink3
        }
      });

      
      if (data) {
        setFormData({
          name: data.name || '',
          photo: data.photo || '',
          bio: data.bio || '',
          role: (data as any).role || '',
          shortBio: data.shortBio || '',
          research: data.research || '',
          projects: data.projects || '',
          references: data.references || '',
          training: data.training || '',
          expertise: data.expertise || '',
          experience: data.experience || '',
          education: data.education || '',
          company: data.company || '',
          linkedin: data.linkedin || '',
          scholar: (data as any).scholar || '',
          twitter: data.twitter || '',
          website: data.website || '',
          // Reference Projects (up to 5)
          referenceProjectTitle1: (data as any).referenceProjectTitle1 || '',
          referenceProjectSummary1: (data as any).referenceProjectSummary1 || '',
          referenceProjectLink1: (data as any).referenceProjectLink1 || '',
          referenceProjectTitle2: (data as any).referenceProjectTitle2 || '',
          referenceProjectSummary2: (data as any).referenceProjectSummary2 || '',
          referenceProjectLink2: (data as any).referenceProjectLink2 || '',
          referenceProjectTitle3: (data as any).referenceProjectTitle3 || '',
          referenceProjectSummary3: (data as any).referenceProjectSummary3 || '',
          referenceProjectLink3: (data as any).referenceProjectLink3 || '',
          referenceProjectTitle4: (data as any).referenceProjectTitle4 || '',
          referenceProjectSummary4: (data as any).referenceProjectSummary4 || '',
          referenceProjectLink4: (data as any).referenceProjectLink4 || '',
          referenceProjectTitle5: (data as any).referenceProjectTitle5 || '',
          referenceProjectSummary5: (data as any).referenceProjectSummary5 || '',
          referenceProjectLink5: (data as any).referenceProjectLink5 || '',
          // Press Articles (up to 5)
          pressArticleTitle1: (data as any).pressArticleTitle1 || '',
          pressArticleUrl1: (data as any).pressArticleUrl1 || '',
          pressArticleTitle2: (data as any).pressArticleTitle2 || '',
          pressArticleUrl2: (data as any).pressArticleUrl2 || '',
          pressArticleTitle3: (data as any).pressArticleTitle3 || '',
          pressArticleUrl3: (data as any).pressArticleUrl3 || '',
          pressArticleTitle4: (data as any).pressArticleTitle4 || '',
          pressArticleUrl4: (data as any).pressArticleUrl4 || '',
          pressArticleTitle5: (data as any).pressArticleTitle5 || '',
          pressArticleUrl5: (data as any).pressArticleUrl5 || '',
          isFemale: (data as any).isFemale ?? false,
          isActive: data.isActive ?? true
        });



        // Do not set photoPreview directly; it will be resolved from formData.photo via useEffect
      }
    } catch (error) {
      console.error('❌ Error loading tutor:', error);
      setMessage({
        type: 'error',
        text: `Fehler beim Laden des Tutors: ${error instanceof Error ? error.message : 'Unknown error'}`
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: field === 'isActive' ? value === 'true' : field === 'isFemale' ? value === 'true' : value
    }));
    
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setPhotoFile(file);
      
      // Create preview URL
      const previewUrl = URL.createObjectURL(file);
      setPhotoPreview(previewUrl);
      
      // Clear any photo URL errors
      if (errors.photo) {
        setErrors(prev => ({ ...prev, photo: '' }));
      }
    }
  };

  const clearPhoto = () => {
    // Clean up blob URL if it exists
    if (photoPreview && photoPreview.startsWith('blob:')) {
      URL.revokeObjectURL(photoPreview);
    }
    
    setPhotoFile(null);
    setPhotoPreview('');
    handleInputChange('photo', '');
    // Clear the file input
    const fileInput = document.getElementById('photoFile') as HTMLInputElement;
    if (fileInput) fileInput.value = '';
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name ist erforderlich';
    }
    
    // Biography, Expertise, and Experience are no longer mandatory
    
    // Education is no longer mandatory
    
    if (!formData.company.trim()) {
      newErrors.company = 'Unternehmen ist erforderlich';
    }
    
    // Validate URLs if provided
    if (formData.linkedin && !isValidUrl(formData.linkedin)) {
      newErrors.linkedin = 'Ungültige LinkedIn URL';
    }
    
    if (formData.twitter && !isValidUrl(formData.twitter)) {
      newErrors.twitter = 'Ungültige Twitter URL';
    }
    
    if (formData.website && !isValidUrl(formData.website)) {
      newErrors.website = 'Ungültige Website URL';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isValidUrl = (url: string) => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Prevent double submissions
    if (saving) {
      console.warn('⚠️ Save already in progress, ignoring duplicate submission');
      return;
    }
    
    if (!validateForm()) {
      return;
    }
    
    try {
      setSaving(true);
      
      const tutorData = {
        name: formData.name.trim(),
        photo: formData.photo.trim(),
        bio: formData.bio.trim(),
        role: formData.role.trim() || null,
        shortBio: formData.shortBio.trim(),
        research: formData.research.trim() || null,
        projects: formData.projects.trim() || null,
        references: formData.references.trim() || null,
        training: formData.training.trim() || null,
        expertise: formData.expertise.trim(),
        experience: formData.experience.trim(),
        education: formData.education.trim(),
        company: formData.company.trim(),
        linkedin: formData.linkedin.trim() || null,
        scholar: formData.scholar.trim() || null,
        twitter: formData.twitter.trim() || null,
        website: formData.website.trim() || null,
        // Reference Projects (up to 5)
        referenceProjectTitle1: formData.referenceProjectTitle1.trim() || null,
        referenceProjectSummary1: formData.referenceProjectSummary1.trim() || null,
        referenceProjectLink1: formData.referenceProjectLink1.trim() || null,
        referenceProjectTitle2: formData.referenceProjectTitle2.trim() || null,
        referenceProjectSummary2: formData.referenceProjectSummary2.trim() || null,
        referenceProjectLink2: formData.referenceProjectLink2.trim() || null,
        referenceProjectTitle3: formData.referenceProjectTitle3.trim() || null,
        referenceProjectSummary3: formData.referenceProjectSummary3.trim() || null,
        referenceProjectLink3: formData.referenceProjectLink3.trim() || null,
        referenceProjectTitle4: formData.referenceProjectTitle4.trim() || null,
        referenceProjectSummary4: formData.referenceProjectSummary4.trim() || null,
        referenceProjectLink4: formData.referenceProjectLink4.trim() || null,
        referenceProjectTitle5: formData.referenceProjectTitle5.trim() || null,
        referenceProjectSummary5: formData.referenceProjectSummary5.trim() || null,
        referenceProjectLink5: formData.referenceProjectLink5.trim() || null,
        // Press Articles (up to 5)
        pressArticleTitle1: formData.pressArticleTitle1.trim() || null,
        pressArticleUrl1: formData.pressArticleUrl1.trim() || null,
        pressArticleTitle2: formData.pressArticleTitle2.trim() || null,
        pressArticleUrl2: formData.pressArticleUrl2.trim() || null,
        pressArticleTitle3: formData.pressArticleTitle3.trim() || null,
        pressArticleUrl3: formData.pressArticleUrl3.trim() || null,
        pressArticleTitle4: formData.pressArticleTitle4.trim() || null,
        pressArticleUrl4: formData.pressArticleUrl4.trim() || null,
        pressArticleTitle5: formData.pressArticleTitle5.trim() || null,
        pressArticleUrl5: formData.pressArticleUrl5.trim() || null,
        isFemale: !!formData.isFemale,
        isActive: formData.isActive
      };


      
      let result;
      
      if (isEditMode) {
        console.log('📝 Updating tutor:', selectedTutorId);
        console.log('📊 Tutor data being sent:', tutorData);
        result = await client.models.Tutor.update({
          id: selectedTutorId,
          ...tutorData
        });
        console.log('✅ Tutor updated:', result);
      } else {
        console.log('📝 Creating new tutor');
        result = await client.models.Tutor.create(tutorData);
        console.log('✅ Tutor created:', result);
      }
      
      setMessage({
        type: 'success',
        text: `Tutor wurde erfolgreich ${isEditMode ? 'aktualisiert' : 'erstellt'}.`
      });
      
      // Call onSave callback if provided
      if (onSave && result.data) {
        onSave(result.data as Tutor);
      }
      
      // Clear message after 3 seconds
      setTimeout(() => setMessage(null), 3000);
      
      // Stay on editor after save; do not navigate away automatically
      
    } catch (error) {
      console.error('❌ Error saving tutor:', error);
      setMessage({
        type: 'error',
        text: `Fehler beim Speichern des Tutors: ${error instanceof Error ? error.message : 'Unknown error'}`
      });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    // Clear all fields to initial empty values
    setFormData({
      name: '',
      photo: '',
      bio: '',
      role: '',
      shortBio: '',
      research: '',
      projects: '',
      references: '',
      training: '',
      expertise: '',
      experience: '',
      education: '',
      company: '',
      linkedin: '',
      scholar: '',
      twitter: '',
      website: '',
      // Reference Projects (up to 5)
      referenceProjectTitle1: '',
      referenceProjectSummary1: '',
      referenceProjectLink1: '',
      referenceProjectTitle2: '',
      referenceProjectSummary2: '',
      referenceProjectLink2: '',
      referenceProjectTitle3: '',
      referenceProjectSummary3: '',
      referenceProjectLink3: '',
      referenceProjectTitle4: '',
      referenceProjectSummary4: '',
      referenceProjectLink4: '',
      referenceProjectTitle5: '',
      referenceProjectSummary5: '',
      referenceProjectLink5: '',
      // Press Articles (up to 5)
      pressArticleTitle1: '',
      pressArticleUrl1: '',
      pressArticleTitle2: '',
      pressArticleUrl2: '',
      pressArticleTitle3: '',
      pressArticleUrl3: '',
      pressArticleTitle4: '',
      pressArticleUrl4: '',
      pressArticleTitle5: '',
      pressArticleUrl5: '',
      isFemale: false,
      isActive: true
    });
    if (photoPreview && photoPreview.startsWith('blob:')) {
      URL.revokeObjectURL(photoPreview);
    }
    setPhotoFile(null);
    setPhotoPreview('');
    setErrors({});
  };

  const createSlug = (text: string) => {
    return (text || '')
      .toLowerCase()
      .replace(/[^a-z0-9\-\s]+/g, '')
      .trim()
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  // Inject selector into header
  React.useEffect(() => {
    const container = document.getElementById('header-selector-container');
    if (container) {
      container.innerHTML = `
        <select
          id="tutorSelect"
          style="padding: 8px 12px; border: 1px solid rgba(255, 255, 255, 0.3); border-radius: 6px; font-size: 14px; min-width: 200px; background: rgba(255, 255, 255, 0.95); color: #374151; box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);"
        >
          <option value="" ${selectedTutorId === '' ? 'selected' : ''}>Create New Tutor</option>
          ${availableTutors.map(tutor => 
            `<option value="${tutor.id}" ${selectedTutorId === tutor.id ? 'selected' : ''}>${tutor.name}</option>`
          ).join('')}
        </select>
      `;

      // Add event listeners
      const selectElement = container.querySelector('select');
      if (selectElement) {
        selectElement.addEventListener('change', (e) => {
          handleTutorSelection((e.target as HTMLSelectElement).value);
        });
      }
    }

    return () => {
      // Cleanup
      if (container) {
        container.innerHTML = '';
      }
    };
  }, [selectedTutorId, availableTutors]);

  if (loading) {
    return (
      <div className="admin-form">
        <div style={{ textAlign: 'center', padding: '48px' }}>
          <div>Loading tutor...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-form admin-editor-content">
      <form onSubmit={handleSubmit}>
        <div className="form-grid">
          <div className="form-field">
            <label htmlFor="name">Tutor Name *</label>
            <input
              id="name"
              type="text"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              placeholder="Enter tutor name"
              className={errors.name ? 'error' : ''}
            />
            {errors.name && (
              <div className="error-message">{errors.name}</div>
            )}
          </div>

          <div className="form-field">
            <label htmlFor="company">Company *</label>
            <input
              id="company"
              type="text"
              value={formData.company}
              onChange={(e) => handleInputChange('company', e.target.value)}
              placeholder="Enter company name"
              className={errors.company ? 'error' : ''}
            />
            {errors.company && (
              <div className="error-message">{errors.company}</div>
            )}
          </div>

          <div className="form-field">
            <label htmlFor="role">Role (one sentence)</label>
            <input
              id="role"
              type="text"
              value={formData.role}
              onChange={(e) => handleInputChange('role', e.target.value)}
              placeholder="e.g., Didaktische Leitung"
            />
          </div>

          <div className="form-field">
            <label htmlFor="isFemale" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <input
                type="checkbox"
                id="isFemale"
                checked={!!formData.isFemale}
                onChange={(e) => handleInputChange('isFemale', e.target.checked.toString())}
                style={{ margin: 0 }}
              />
              Female
            </label>
            <small style={{ color: '#6b7280', fontSize: '12px', marginTop: '4px', display: 'block' }}>
              If unchecked, tutor is considered male by default
            </small>
          </div>

          <div className="form-field">
            <label htmlFor="isActive" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <input
                type="checkbox"
                id="isActive"
                checked={formData.isActive}
                onChange={(e) => handleInputChange('isActive', e.target.checked.toString())}
                style={{ margin: 0 }}
              />
              Active Tutor
            </label>
            <small style={{ color: '#6b7280', fontSize: '12px', marginTop: '4px', display: 'block' }}>
              Only active tutors are displayed publicly
            </small>
          </div>


          {/* Profile Photo Section - Full Width */}
          <div style={{ 
            gridColumn: '1 / -1',
            marginTop: '1rem',
            padding: '2rem',
            background: '#f8f9fa',
            borderRadius: '8px'
          }}>
            <div 
              style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                cursor: 'pointer',
                marginBottom: collapsibleStates.profilePhoto ? '0' : '1rem'
              }}
              onClick={() => toggleCollapsibleSection('profilePhoto')}
            >
              <h3 style={{ color: '#008F99', margin: '0', fontSize: '1.2rem', fontWeight: '600' }}>
                Profilfoto
              </h3>
              <span style={{ 
                color: '#008F99', 
                fontSize: '1.2rem', 
                fontWeight: 'bold',
                transform: collapsibleStates.profilePhoto ? 'rotate(-90deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s ease'
              }}>
                ▼
              </span>
            </div>

            {!collapsibleStates.profilePhoto && (
              <div className="form-field">
                <label htmlFor="photoFile">Profile Photo</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {/* Existing S3 photos selector */}
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <select
                      value={formData.photo || ''}
                      onChange={(e) => handleInputChange('photo', e.target.value)}
                    >
                      <option value="">Choose existing profile photo…</option>
                      {existingPhotoItems.map(item => (
                        <option key={item.key} value={item.key}>{item.label}</option>
                      ))}
                    </select>
                    <button
                      type="button"
                      className="admin-btn"
                      onClick={loadExistingTutorPhotos}
                      disabled={loadingExistingPhotos}
                    >
                      {loadingExistingPhotos ? 'Refreshing…' : 'Refresh list'}
                    </button>
                  </div>

                  <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                    <input
                      id="photoFile"
                      type="file"
                      accept=".jpg,.jpeg,.png,.svg,image/*"
                      onChange={(e) => {
                        const file = e.target.files?.[0] || null;
                        setPhotoFile(file);
                        if (file) {
                          const previewUrl = URL.createObjectURL(file);
                          setPhotoPreview(previewUrl);
                          if (errors.photo) setErrors(prev => ({ ...prev, photo: '' }));
                        }
                      }}
                      style={{
                        padding: '8px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px',
                        flex: 1
                      }}
                    />
                    {(photoFile || photoPreview) && (
                      <button
                        type="button"
                        onClick={clearPhoto}
                        style={{
                          padding: '8px 12px',
                          border: '1px solid #dc2626',
                          borderRadius: '6px',
                          background: 'white',
                          color: '#dc2626',
                          fontSize: '12px',
                          cursor: 'pointer'
                        }}
                      >
                        Clear
                      </button>
                    )}
                  </div>

                  {/* Upload button to S3 */}
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <button
                      type="button"
                      className="admin-btn"
                      disabled={!photoFile || photoUploading}
                      onClick={async () => {
                        if (!photoFile) return;
                        try {
                          setPhotoUploading(true);
                          const safeName = (formData.name || 'tutor')
                            .toLowerCase()
                            .replace(/[^a-z0-9\-]+/g, '-')
                            .replace(/^-+|-+$/g, '');
                          const timestamp = Date.now();
                          // Do not prefix with 'public/' – accessLevel public adds it automatically
                          const key = `tutors/${safeName}/${timestamp}_${photoFile.name}`;
                          await uploadData({ key, data: photoFile, options: { contentType: photoFile.type } }).result;
                          const { url } = await getUrl({ path: `public/${key}` });
                          handleInputChange('photo', key);
                          setPhotoPreview(url.toString());
                          setMessage({ type: 'success', text: 'Profile photo uploaded successfully.' });
                          loadExistingTutorPhotos();
                        } catch (err: any) {
                          console.error('Photo upload failed:', err);
                          setMessage({ type: 'error', text: `Photo upload failed: ${err?.message || err}` });
                        } finally {
                          setPhotoUploading(false);
                        }
                      }}
                    >
                      {photoUploading ? 'Uploading…' : 'Upload'}
                    </button>
                  </div>

                  {/* S3 Diagnostics */}
                  <div style={{ marginTop: '8px', display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <button
                      type="button"
                      className="admin-btn"
                      disabled={s3CheckRunning}
                      onClick={async () => {
                        try {
                          setS3CheckRunning(true);
                          setS3CheckMessage('Listing public/tutors/ …');
                          const { items } = await list({ path: 'public/tutors/', options: { recursive: true } } as any);
                          const fileCount = (items || []).filter(it => (it.path || '').endsWith('/') === false).length;
                          setS3CheckMessage(`OK: Found ${fileCount} item(s) under public/tutors/`);
                        } catch (e: any) {
                          console.error('S3 list failed:', e);
                          setS3CheckMessage(`List failed: ${e?.message || e}`);
                        } finally {
                          setS3CheckRunning(false);
                        }
                      }}
                    >
                      Check S3 list
                    </button>
                    <button
                      type="button"
                      className="admin-btn"
                      disabled={s3CheckRunning}
                      onClick={async () => {
                        try {
                          setS3CheckRunning(true);
                          setS3CheckMessage('Uploading test object …');
                          const safeName = (formData.name || 'tutor')
                            .toLowerCase()
                            .replace(/[^a-z0-9\-]+/g, '-')
                            .replace(/^-+|-+$/g, '');
                          const timestamp = Date.now();
                          const key = `public/tutors/_diagnostics/${safeName}_${timestamp}.txt`;
                          const blob = new Blob([`MintKitchen S3 test upload at ${new Date().toISOString()}`], { type: 'text/plain' });
                          await uploadData({ key, data: blob, options: { contentType: 'text/plain' } }).result;
                          const { url } = await getUrl({ path: key });
                          setS3TestKey(key.startsWith('public/') ? key.substring('public/'.length) : key);
                          setS3TestUrl(url.toString());
                          setS3CheckMessage('OK: Test object uploaded');
                        } catch (e: any) {
                          console.error('S3 test upload failed:', e);
                          setS3CheckMessage(`Upload failed: ${e?.message || e}`);
                        } finally {
                          setS3CheckRunning(false);
                        }
                      }}
                    >
                      Upload test object
                    </button>
                    {s3CheckMessage && (
                      <span style={{ fontSize: '12px', color: '#374151' }}>{s3CheckMessage}</span>
                    )}
                    {s3TestUrl && (
                      <a
                        href={s3TestUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ fontSize: '12px', color: '#008F99' }}
                        title={s3TestKey}
                      >
                        Open test object
                      </a>
                    )}
                  </div>
                  
                  {photoPreview && (
                    <div style={{
                      padding: '12px',
                      border: '1px solid #e5e7eb',
                      borderRadius: '6px',
                      background: '#f9fafb',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px'
                    }}>
                      <div style={{
                        width: '48px',
                        height: '48px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: 'white',
                        border: '1px solid #d1d5db',
                        borderRadius: '50%',
                        overflow: 'hidden'
                      }}>
                        <img
                          src={photoPreview}
                          alt="Photo preview"
                          style={{
                            maxWidth: '100%',
                            maxHeight: '100%',
                            objectFit: 'cover',
                            borderRadius: '50%'
                          }}
                          onError={(e) => {
                            console.error('Failed to load photo preview:', photoPreview);
                            (e.target as HTMLImageElement).style.display = 'none';
                          }}
                        />
                      </div>
                      <div>
                        <div style={{ fontSize: '14px', fontWeight: 500, color: '#374151' }}>
                          Photo Preview
                        </div>
                      </div>
                    </div>
                  )}

                  <input
                    type="text"
                    value={formData.photo}
                    onChange={(e) => handleInputChange('photo', e.target.value)}
                    placeholder="or enter key/URL manually (e.g., tutors/jane/1234_file.jpg)"
                    style={{
                      padding: '8px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px',
                      fontStyle: 'italic',
                      color: '#6b7280'
                    }}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Profile Content Section - Full Width */}
          <div style={{ 
            gridColumn: '1 / -1',
            marginTop: '1rem',
            padding: '2rem',
            background: '#f8f9fa',
            borderRadius: '8px'
          }}>
            <div 
              style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                cursor: 'pointer',
                marginBottom: collapsibleStates.profileContent ? '0' : '1rem'
              }}
              onClick={() => toggleCollapsibleSection('profileContent')}
            >
              <h3 style={{ color: '#008F99', margin: '0', fontSize: '1.2rem', fontWeight: '600' }}>
                Bio
              </h3>
              <span style={{ 
                color: '#008F99', 
                fontSize: '1.2rem', 
                fontWeight: 'bold',
                transform: collapsibleStates.profileContent ? 'rotate(-90deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s ease'
              }}>
                ▼
              </span>
            </div>

            {!collapsibleStates.profileContent && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1.5rem' }}>
                <div className="form-field">
                  <label htmlFor="education">Education</label>
                  <textarea
                    id="education"
                    value={formData.education}
                    onChange={(e) => handleInputChange('education', e.target.value)}
                    placeholder="e.g., Ph.D. Computer Science, MIT"
                    rows={4}
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px',
                      fontFamily: 'inherit',
                      resize: 'vertical'
                    }}
                    className={errors.education ? 'error' : ''}
                  />
                  {errors.education && (
                    <div className="error-message">{errors.education}</div>
                  )}
                </div>

                <div className="form-field">
                  <label htmlFor="experience">Experience</label>
                  <textarea
                    id="experience"
                    value={formData.experience}
                    onChange={(e) => handleInputChange('experience', e.target.value)}
                    placeholder="e.g., 10+ years in AI research"
                    rows={4}
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px',
                      fontFamily: 'inherit',
                      resize: 'vertical'
                    }}
                    className={errors.experience ? 'error' : ''}
                  />
                  {errors.experience && (
                    <div className="error-message">{errors.experience}</div>
                  )}
                </div>

                <div className="form-field">
                  <label htmlFor="expertise">Expertise</label>
                  <textarea
                    id="expertise"
                    value={formData.expertise}
                    onChange={(e) => handleInputChange('expertise', e.target.value)}
                    placeholder="e.g., Machine Learning, NLP, Computer Vision"
                    rows={4}
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px',
                      fontFamily: 'inherit',
                      resize: 'vertical'
                    }}
                    className={errors.expertise ? 'error' : ''}
                  />
                  <small style={{ color: '#6b7280', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                    Separate multiple areas with commas
                  </small>
                </div>

                <div className="form-field">
                  <label htmlFor="bio">Biography</label>
                  <textarea
                    id="bio"
                    value={formData.bio}
                    onChange={(e) => handleInputChange('bio', e.target.value)}
                    placeholder="Professional background and experience..."
                    rows={4}
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px',
                      fontFamily: 'inherit',
                      resize: 'vertical'
                    }}
                    className={errors.bio ? 'error' : ''}
                  />
                  {errors.bio && (
                    <div className="error-message">{errors.bio}</div>
                  )}
                </div>

                <div className="form-field">
                  <label htmlFor="shortBio">Short Bio</label>
                  <textarea
                    id="shortBio"
                    value={formData.shortBio}
                    onChange={(e) => handleInputChange('shortBio', e.target.value)}
                    placeholder="Brief professional summary (2-3 sentences)"
                    rows={3}
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px',
                      fontFamily: 'inherit',
                      resize: 'vertical'
                    }}
                  />
                </div>

                <div className="form-field">
                  <label htmlFor="research">Research Areas</label>
                  <textarea
                    id="research"
                    value={formData.research}
                    onChange={(e) => handleInputChange('research', e.target.value)}
                    placeholder="Research interests and areas of focus..."
                    rows={3}
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px',
                      fontFamily: 'inherit',
                      resize: 'vertical'
                    }}
                  />
                </div>

                <div className="form-field">
                  <label htmlFor="projects">Notable Projects</label>
                  <textarea
                    id="projects"
                    value={formData.projects}
                    onChange={(e) => handleInputChange('projects', e.target.value)}
                    placeholder="Key projects, achievements, and notable work..."
                    rows={3}
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px',
                      fontFamily: 'inherit',
                      resize: 'vertical'
                    }}
                  />
                </div>

                <div className="form-field">
                  <label htmlFor="references">References & Testimonials</label>
                  <textarea
                    id="references"
                    value={formData.references}
                    onChange={(e) => handleInputChange('references', e.target.value)}
                    placeholder="Professional references, testimonials, or endorsements..."
                    rows={3}
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px',
                      fontFamily: 'inherit',
                      resize: 'vertical'
                    }}
                  />
                </div>

                <div className="form-field">
                  <label htmlFor="training">Training & Certifications</label>
                  <textarea
                    id="training"
                    value={formData.training}
                    onChange={(e) => handleInputChange('training', e.target.value)}
                    placeholder="Professional training, certifications, and qualifications..."
                    rows={3}
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px',
                      fontFamily: 'inherit',
                      resize: 'vertical'
                    }}
                  />
                </div>
              </div>
            )}
          </div>

          

        </div>

        {/* Profile Links Section - Full Width */}
        <div style={{ 
          marginTop: '2rem', 
          padding: '2rem', 
          background: '#f8f9fa', 
          borderRadius: '8px',
          width: '100%',
          gridColumn: '1 / -1'
        }}>
          <div 
            style={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              cursor: 'pointer',
              marginBottom: collapsibleStates.profileLinks ? '0' : '1rem'
            }}
            onClick={() => toggleCollapsibleSection('profileLinks')}
          >
            <h3 style={{ color: '#008F99', margin: '0', fontSize: '1.2rem', fontWeight: '600' }}>
              Links
            </h3>
            <span style={{ 
              color: '#008F99', 
              fontSize: '1.2rem', 
              fontWeight: 'bold',
              transform: collapsibleStates.profileLinks ? 'rotate(-90deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s ease'
            }}>
              ▼
            </span>
          </div>

          {!collapsibleStates.profileLinks && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1.5rem' }}>
              <div className="form-field">
                <label htmlFor="linkedin">LinkedIn URL</label>
                <input
                  id="linkedin"
                  type="url"
                  value={formData.linkedin}
                  onChange={(e) => handleInputChange('linkedin', e.target.value)}
                  placeholder="https://linkedin.com/in/username"
                  className={errors.linkedin ? 'error' : ''}
                />
                {errors.linkedin && (
                  <div className="error-message">{errors.linkedin}</div>
                )}
              </div>

              <div className="form-field">
                <label htmlFor="scholar">Google Scholar URL</label>
                <input
                  id="scholar"
                  type="url"
                  value={formData.scholar}
                  onChange={(e) => handleInputChange('scholar', e.target.value)}
                  placeholder="https://scholar.google.com/citations?user=..."
                />
              </div>

              <div className="form-field">
                <label htmlFor="website">Website URL</label>
                <input
                  id="website"
                  type="url"
                  value={formData.website}
                  onChange={(e) => handleInputChange('website', e.target.value)}
                  placeholder="https://tutor-website.com"
                  className={errors.website ? 'error' : ''}
                />
                {errors.website && (
                  <div className="error-message">{errors.website}</div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Reference Projects Section - Full Width */}
        <div style={{ 
          marginTop: '2rem', 
          padding: '2rem', 
          background: '#f8f9fa', 
          borderRadius: '8px',
          width: '100%',
          gridColumn: '1 / -1' // Span all grid columns
        }}>
          <div 
            style={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              cursor: 'pointer',
              marginBottom: collapsibleStates.referenceProjects ? '0' : '1rem'
            }}
            onClick={() => toggleCollapsibleSection('referenceProjects')}
          >
            <h3 style={{ color: '#008F99', margin: '0', fontSize: '1.2rem', fontWeight: '600' }}>
              Referenzprojekte
            </h3>
            <span style={{ 
              color: '#008F99', 
              fontSize: '1.2rem', 
              fontWeight: 'bold',
              transform: collapsibleStates.referenceProjects ? 'rotate(-90deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s ease'
            }}>
              ▼
            </span>
          </div>
          
          {!collapsibleStates.referenceProjects && (
            <>
          
          {[1, 2, 3, 4, 5].map(num => (
            <div key={num} style={{ 
              marginBottom: '2rem', 
              padding: '1.5rem', 
              background: 'white', 
              borderRadius: '8px', 
              border: '1px solid #e5e7eb',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
            }}>
              <h4 style={{ color: '#374151', marginBottom: '1.5rem', fontSize: '1.1rem', fontWeight: '500' }}>
                Projekt {num}
              </h4>
              
              {/* Project fields in a more spacious layout */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '1.5rem', alignItems: 'start' }}>
                <div className="form-field">
                  <label htmlFor={`referenceProjectTitle${num}`}>Projekttitel</label>
                  <input
                    id={`referenceProjectTitle${num}`}
                    type="text"
                    value={formData[`referenceProjectTitle${num}` as keyof typeof formData] as string}
                    onChange={(e) => handleInputChange(`referenceProjectTitle${num}`, e.target.value)}
                    placeholder="z.B. KI-basierte Datenanalyse für Automobilindustrie"
                    style={{ width: '100%' }}
                  />
                </div>

                <div className="form-field">
                  <label htmlFor={`referenceProjectLink${num}`}>Projekt-Link (optional)</label>
                  <input
                    id={`referenceProjectLink${num}`}
                    type="url"
                    value={formData[`referenceProjectLink${num}` as keyof typeof formData] as string}
                    onChange={(e) => handleInputChange(`referenceProjectLink${num}`, e.target.value)}
                    placeholder="https://projekt-website.com oder GitHub-Link"
                    style={{ width: '100%' }}
                  />
                </div>
              </div>

              <div className="form-field" style={{ marginTop: '1rem' }}>
                <label htmlFor={`referenceProjectSummary${num}`}>Projektbeschreibung</label>
                <textarea
                  id={`referenceProjectSummary${num}`}
                  value={formData[`referenceProjectSummary${num}` as keyof typeof formData] as string}
                  onChange={(e) => handleInputChange(`referenceProjectSummary${num}`, e.target.value)}
                  placeholder="Kurze Beschreibung des Projekts, der verwendeten Technologien und der erzielten Ergebnisse..."
                  rows={4}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontFamily: 'inherit',
                    resize: 'vertical',
                    minHeight: '100px'
                  }}
                />
              </div>
            </div>
          ))}
            </>
          )}
        </div>

        {/* Press Articles Section - Full Width */}
        <div style={{ 
          marginTop: '2rem', 
          padding: '2rem', 
          background: '#f8f9fa', 
          borderRadius: '8px',
          width: '100%',
          gridColumn: '1 / -1' // Span all grid columns
        }}>
          <div 
            style={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              cursor: 'pointer',
              marginBottom: collapsibleStates.pressArticles ? '0' : '1rem'
            }}
            onClick={() => toggleCollapsibleSection('pressArticles')}
          >
            <h3 style={{ color: '#008F99', margin: '0', fontSize: '1.2rem', fontWeight: '600' }}>
              Presseartikel
            </h3>
            <span style={{ 
              color: '#008F99', 
              fontSize: '1.2rem', 
              fontWeight: 'bold',
              transform: collapsibleStates.pressArticles ? 'rotate(-90deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s ease'
            }}>
              ▼
            </span>
          </div>
          
          {!collapsibleStates.pressArticles && (
            <>
          
          {[1, 2, 3, 4, 5].map(num => (
            <div key={num} style={{ 
              marginBottom: '2rem', 
              padding: '1.5rem', 
              background: 'white', 
              borderRadius: '8px', 
              border: '1px solid #e5e7eb',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
            }}>
              <h4 style={{ color: '#374151', marginBottom: '1.5rem', fontSize: '1.1rem', fontWeight: '500' }}>
                Artikel {num}
              </h4>
              
              {/* Article fields */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', alignItems: 'start' }}>
                <div className="form-field">
                  <label htmlFor={`pressArticleTitle${num}`}>Artikeltitel</label>
                  <input
                    id={`pressArticleTitle${num}`}
                    type="text"
                    value={formData[`pressArticleTitle${num}` as keyof typeof formData] as string}
                    onChange={(e) => handleInputChange(`pressArticleTitle${num}`, e.target.value)}
                    placeholder="z.B. KI-Revolution in der Bildung"
                    style={{ width: '100%' }}
                  />
                </div>

                <div className="form-field">
                  <label htmlFor={`pressArticleUrl${num}`}>Artikel-URL</label>
                  <input
                    id={`pressArticleUrl${num}`}
                    type="url"
                    value={formData[`pressArticleUrl${num}` as keyof typeof formData] as string}
                    onChange={(e) => handleInputChange(`pressArticleUrl${num}`, e.target.value)}
                    placeholder="https://zeitung.de/artikel-link"
                    style={{ width: '100%' }}
                  />
                </div>
              </div>
            </div>
          ))}
            </>
          )}
        </div>

        <div style={{ marginTop: '2rem' }}>
        </div>

        <div className="form-actions">
          <button
            type="button"
            onClick={handleReset}
            className="admin-btn secondary"
            disabled={saving}
          >
            Reset
          </button>
          <button
            type="button"
            className="admin-btn"
            disabled={!formData.name}
            onClick={() => {
              if (!formData.name) return;
              const slug = createSlug(formData.name);
              if (slug) {
                window.location.href = `/tutors/${slug}`;
              }
            }}
            title={formData.name ? `Visit /tutors/${createSlug(formData.name)}` : 'Enter tutor name to enable'}
          >
            Visit
          </button>
        </div>
      </form>
      
      <StickyFooter
        onSave={() => {
          const form = document.querySelector('form');
          if (form) {
            form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
          }
        }}
        saving={saving}
        message={message}
        onMessageClose={() => setMessage(null)}
      />
    </div>
  );
};

export default AdminTutorEditor;
